import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useBundleEffectiveness } from '@/hooks/useBundleEffectiveness';
import { Award, TrendingUp, DollarSign, BarChart3, Zap } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export function BundleEffectivenessScoring() {
  const { data: bundles, isLoading } = useBundleEffectiveness();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!bundles || bundles.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">No bundle effectiveness data available yet.</p>
        </CardContent>
      </Card>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-chart-1';
    if (score >= 60) return 'text-chart-2';
    if (score >= 40) return 'text-chart-3';
    return 'text-chart-4';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-chart-1/10';
    if (score >= 60) return 'bg-chart-2/10';
    if (score >= 40) return 'bg-chart-3/10';
    return 'bg-chart-4/10';
  };

  const getRankBadge = (index: number) => {
    if (index === 0) return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">🥇 #1</Badge>;
    if (index === 1) return <Badge className="bg-gray-400/20 text-gray-400 border-gray-400/30">🥈 #2</Badge>;
    if (index === 2) return <Badge className="bg-orange-600/20 text-orange-600 border-orange-600/30">🥉 #3</Badge>;
    return <Badge variant="outline">#{index + 1}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5" />
          Bundle Effectiveness Ranking
        </CardTitle>
        <CardDescription>
          Combined score based on conversion rate (50%), revenue (30%), and usage (20%)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <TooltipProvider>
          {bundles.map((bundle, index) => (
            <div
              key={bundle.bundle_id}
              className={`p-4 rounded-lg border transition-all hover:shadow-md ${
                index === 0 ? 'border-primary/30 bg-primary/5' : 'bg-card'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {getRankBadge(index)}
                  <div>
                    <h4 className="font-semibold">{bundle.bundle_name}</h4>
                    <p className="text-sm text-muted-foreground">
                      ${bundle.bundle_price.toLocaleString()} · {bundle.total_deals} deals
                    </p>
                  </div>
                </div>
                <div className={`text-2xl font-bold ${getScoreColor(bundle.effectiveness_score)}`}>
                  {bundle.effectiveness_score}
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-3 gap-4 mb-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={`p-2 rounded-lg ${getScoreBg(bundle.conversion_score)}`}>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                        <TrendingUp className="h-3 w-3" />
                        Conversion
                      </div>
                      <div className="font-semibold">{bundle.conversion_score}</div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{bundle.conversion_rate.toFixed(1)}% win rate ({bundle.won_deals} won)</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={`p-2 rounded-lg ${getScoreBg(bundle.revenue_score)}`}>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                        <DollarSign className="h-3 w-3" />
                        Revenue
                      </div>
                      <div className="font-semibold">{bundle.revenue_score}</div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>${bundle.total_revenue.toLocaleString()} total revenue</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={`p-2 rounded-lg ${getScoreBg(bundle.usage_score)}`}>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                        <BarChart3 className="h-3 w-3" />
                        Usage
                      </div>
                      <div className="font-semibold">{bundle.usage_score}</div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Used {bundle.usage_count} times in deals</p>
                  </TooltipContent>
                </Tooltip>
              </div>

              {/* Effectiveness Bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Effectiveness</span>
                  <span className={getScoreColor(bundle.effectiveness_score)}>
                    {bundle.effectiveness_score >= 80 ? 'Excellent' : 
                     bundle.effectiveness_score >= 60 ? 'Good' : 
                     bundle.effectiveness_score >= 40 ? 'Average' : 'Needs Improvement'}
                  </span>
                </div>
                <Progress value={bundle.effectiveness_score} className="h-2" />
              </div>

              {/* Deal Size Info */}
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <span>Avg deal: ${bundle.avg_deal_size.toLocaleString()}</span>
                <span>Range: ${bundle.deal_size_range.min.toLocaleString()} - ${bundle.deal_size_range.max.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}
